import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Vorgang } from '../types';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { formatDDMMYYYY } from '../shared/dateTime';
import { Signature } from './common/Signature';
import { Gutachtenart, Unterzeichner } from '../shared/constants';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { anredeAuswahl } from '../frontendConstants';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function Regressansprueche({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.regressansprueche}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ regressansprueche: event.target.checked })}
              name="regressansprueche"
              color="primary"
              data-testid="regressanspruecheCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="regressanspruecheLink"
              >
                Übertragung Regressforderung - Sachverständigenrisiko
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <RegressanspruecheModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function RegressanspruecheModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const regressanspruecheUrl = useDateiUrl(vorgang?.unterschriften?.regressansprueche?.key);

  const signDate = vorgang?.unterschriften?.regressansprueche?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Übertragung Regressforderung - Sachverständigenrisiko"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="regressanspruecheModal">
          <Grid size={12}>
            <Typography variant="body2">Hiermit trete ich:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" data-testid="auftraggeber">
              {anredeAuswahl.find((anrede) => anrede.value === vorgang.auftraggeber?.anrede)?.label} {vorgang.auftraggeber?.vorname}{' '}
              {vorgang.auftraggeber?.nachname}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              <b>etwaige</b> (das Sachverständigenrisiko betreffende) Ersatzansprüche gegen das Sachverständigenbüro
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Experts24 Tassone GmbH
              <br />
              Hildender Str. 28
              <br />
              42697 Solingen
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              hinsichtlich einer <b>etwaigen</b> Überhöhung der Sachverständigenrechnung für{' '}
              {vorgang.gutachtenart === Gutachtenart.REPARATURNACHWEIS ? 'den Reparaturnachweis' : 'das Gutachten'} aus Anlass des Schadensereignisses vom
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" data-testid="unfalldatum">
              {formatDDMMYYYY(vorgang.unfalldaten.datum)}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              an den Haftpflichtversicherer des am Unfall beteiligten, gegnerischen Fahrzeuges ab.
              <br />
              Jedoch nur Zug um Zug gegen Erstattung des vollständigen Rechnungsbetrages.
              <br />
              Ich verzichte auf den Zugang der Annahmeerklärung.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={regressanspruecheUrl}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.regressansprueche?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              readOnly={true}
              date={signDate}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
